import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, ClipboardList, Clock, Crown, ListOrdered, Ticket, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, type WorkMode } from '../lib/api';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMinutes, formatNumber, formatShortTime } from '../lib/format';
import { Badge, ErrorState, LoadingBlock, Modal, StatusDot, TimeRange, priorityTone, statusTone } from './ui';
import { WorkModeLabel } from './WorkModeLabel';

/**
 * What one person on Working now is working on — the popup their name opens on
 * the dashboard (operator, 2026-10-27: "when click on employee name i need popup
 * to tell me what is this employee working on (tasks - ticket ... ) and when
 * expected to finish and when start"; then "add also next day tasks if he has").
 * Read-only, like the audit log's viewer: nothing is typed here, so the
 * no-popups rule for forms does not apply. `GET /dashboard/working-now/:userId`.
 */

type WorkKind = 'TASK' | 'LEADER_TASK' | 'TICKET' | 'Q_TASK' | 'MEETING';

interface Named {
  id: string;
  name: string;
  nameAr: string | null;
}

interface WorkItem {
  kind: WorkKind;
  id: string;
  /** A ticket's or a Q Task's number. */
  number: string | null;
  title: string;
  status: string;
  priority: string | null;
  project: (Named & { code: string }) | null;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null } | null;
  /** A Q Task's department, or the one a task was given to whole (`shared`). */
  department: Named | null;
  shared: boolean;
  place: 'OFFICE' | 'OUTSIDE' | 'ONLINE' | null;
  /** When work on it really began. */
  startedAt: string | null;
  /** When it is planned to begin — a meeting's start. */
  startAt: string | null;
  /** When it should be finished — a meeting's end. */
  dueAt: string | null;
  /** When a ticket or a Q Task reached them. */
  assignedAt: string | null;
  isOverdue: boolean;
}

interface PersonWork {
  user: { id: string; fullName: string; fullNameAr: string | null; jobTitle: string | null; workMode: WorkMode };
  /** The company's today and tomorrow, which the parts are cut by. */
  days: { today: string; tomorrow: string };
  now: WorkItem[];
  today: WorkItem[];
  tomorrow: WorkItem[];
}

/** Who the popup is about, as Working now shows them. */
export interface PersonOnShift {
  user: { id: string; fullName: string; fullNameAr: string | null };
  dot: 'online' | 'idle' | 'alert';
  /** What they are doing, in words: Working, the break (over its limit), why they are away. */
  doing: string;
  /** Since when: their shift's start, or the break's, or the time they went away. */
  since: string;
  /** Red, as on the chip: a break past its limit, or away from their computer. */
  alert: boolean;
  workedMinutes?: number;
}

const KIND_ICONS: Record<WorkKind, LucideIcon> = {
  TASK: ClipboardList,
  LEADER_TASK: Crown,
  TICKET: Ticket,
  Q_TASK: ListOrdered,
  MEETING: CalendarClock,
};

const KIND_LINKS: Record<WorkKind, (id: string) => string> = {
  TASK: (id) => `/tasks/${id}`,
  LEADER_TASK: (id) => `/leader-tasks/${id}`,
  TICKET: (id) => `/tickets/${id}`,
  Q_TASK: (id) => `/q-tasks/${id}`,
  MEETING: (id) => `/meetings/${id}`,
};

export function PersonWorkDialog({ person, onClose }: { person: PersonOnShift; onClose: () => void }) {
  const { t, locale, localized } = useI18n();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'working-now', person.user.id],
    queryFn: () => api.get<PersonWork>(`/dashboard/working-now/${person.user.id}`),
    // With the dashboard behind it, which re-pulls every half minute.
    refetchInterval: 30_000,
  });

  return (
    <Modal open onClose={onClose} title={localized(person.user)} size="lg">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-muted">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <StatusDot tone={person.dot} />
          <span className={person.alert ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]'}>
            {`${person.doing} · `}
            <Filled text={t('attendance.since')} slot="time">
              <bdi>{formatShortTime(person.since, locale)}</bdi>
            </Filled>
          </span>
        </span>
        {person.workedMinutes != null && (
          <span className="inline-flex items-center gap-1 tabular">
            <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {`${t('attendance.worked')} ${formatMinutes(person.workedMinutes, locale)}`}
          </span>
        )}
        {data?.user.jobTitle && <span dir="auto">{data.user.jobTitle}</span>}
        {data && <WorkModeLabel mode={data.user.workMode} />}
      </div>

      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        data && (
          <div className="mt-5 space-y-5">
            <WorkPart title={t('dash.workNow')} items={data.now} empty={t('dash.workNothingNow')} />
            <WorkPart title={t('dash.workToday')} items={data.today} empty={t('dash.workNothingToday')} />
            <WorkPart
              title={t('dash.workTomorrow')}
              // Noon: the date, whatever the reader's timezone.
              date={formatDate(`${data.days.tomorrow}T12:00:00`, locale)}
              items={data.tomorrow}
              empty={t('dash.workNothingTomorrow')}
            />
          </div>
        )
      )}
    </Modal>
  );
}

/** One part of their day — now, the rest of today, tomorrow — under its name and count. */
function WorkPart({ title, date, items, empty }: { title: string; date?: string; items: WorkItem[]; empty: string }) {
  const { locale } = useI18n();
  return (
    <section>
      <h3 className="flex flex-wrap items-center gap-x-2 text-overline uppercase text-[var(--text-tertiary)]">
        {title}
        <span className="tabular">{formatNumber(items.length, locale)}</span>
        {date && <span className="normal-case tracking-normal text-caption text-muted">· {date}</span>}
      </h3>
      {items.length === 0 ? (
        <p className="mt-1.5 text-body-sm text-muted">{empty}</p>
      ) : (
        <ul className="mt-2 overflow-hidden rounded-md border hairline">
          {items.map((item) => (
            <WorkRow key={`${item.kind}:${item.id}`} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** A sentence of the dictionary with its one placeholder filled by a node — a time in its own `<bdi>`. */
function Filled({ text, slot, children }: { text: string; slot: string; children: ReactNode }) {
  const [before, after = ''] = text.split(`{${slot}}`);
  return (
    <>
      {before}
      {children}
      {after}
    </>
  );
}

/**
 * "today, 10:02", "tomorrow, 09:00", "yesterday, 16:30" — else the date and the
 * time; with `end`, the range from it to that. Each time isolated, never forced
 * left to right: "10:00 ص" keeps its own order in Arabic (2026-10-27).
 */
function useWhen() {
  const { t, locale } = useI18n();
  return (value: string, end?: string | null): ReactNode => {
    const instant = new Date(value);
    const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const shifted = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() + days);
      return dayKey(d);
    };
    const day = dayKey(instant);
    const near: TranslationKey | null =
      day === shifted(0)
        ? 'dash.workTodayAt'
        : day === shifted(1)
          ? 'dash.workTomorrowAt'
          : day === shifted(-1)
            ? 'dash.workYesterdayAt'
            : null;
    const time =
      end === undefined ? <bdi>{formatShortTime(instant, locale)}</bdi> : <TimeRange from={instant} to={end} />;
    if (near) {
      return (
        <Filled text={t(near)} slot="time">
          {time}
        </Filled>
      );
    }
    return end === undefined ? (
      <bdi>{formatDateTime(instant, locale)}</bdi>
    ) : (
      <>
        <bdi>{formatDate(instant, locale)}</bdi> {time}
      </>
    );
  };
}

/**
 * One piece of their work: what it is and where it belongs, its status, when it
 * started — or is planned to, or reached them — and when it should be finished.
 * Opens its own page.
 */
function WorkRow({ item }: { item: WorkItem }) {
  const { t, locale, localized } = useI18n();
  const when = useWhen();
  const Icon = KIND_ICONS[item.kind];

  // Where it belongs, after what it is: the meeting's place, the Q Task's department, the
  // ticket's client or project, the task's project — and whoever shares a department's task.
  const where = [
    item.kind === 'MEETING'
      ? item.place && t(`meetings.place.${item.place}` as TranslationKey)
      : item.kind === 'Q_TASK'
        ? item.department && localized(item.department)
        : item.kind === 'TICKET' && item.client
          ? locale === 'ar'
            ? (item.client.companyNameAr ?? item.client.companyName)
            : item.client.companyName
          : item.project
            ? localized(item.project)
            : t('common.general'),
    item.shared && item.department ? t('tasks.everyoneIn', { department: localized(item.department) }) : null,
  ].filter(Boolean);
  const status =
    item.kind === 'MEETING' ? t(`meetings.status.${item.status}` as TranslationKey) : enumLabel(item.status, locale);
  // When it began, or is planned to, or reached them — the first of those it has.
  const [begunText, begunAt]: [TranslationKey, string] | [null, null] = item.startedAt
    ? ['dash.workStarted', item.startedAt]
    : item.startAt
      ? ['dash.workPlannedStart', item.startAt]
      : item.assignedAt
        ? ['dash.workAssigned', item.assignedAt]
        : [null, null];

  return (
    <li className="border-b hairline last:border-0">
      <Link to={KIND_LINKS[item.kind](item.id)} className="flex gap-3 px-3 py-2.5 hover:bg-[var(--surface-sunken)]">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline gap-2">
            {item.number && (
              <span className="shrink-0 font-mono text-mono-sm text-primary-600 dark:text-primary-400" dir="ltr">
                {item.number}
              </span>
            )}
            <span className="truncate text-body-sm font-medium text-[var(--text-primary)]" dir="auto">
              {item.title}
            </span>
          </span>
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-muted">
            <span className="shrink-0">{t(`dash.workKind.${item.kind}` as TranslationKey)}</span>
            {where.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span className="truncate" dir="auto">
                  {where.join(' · ')}
                </span>
              </>
            )}
          </span>
          <span className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-caption tabular">
            {item.kind === 'MEETING' ? (
              <span className="text-[var(--text-secondary)]">{when(item.startAt!, item.dueAt)}</span>
            ) : (
              <>
                {begunText && (
                  <span className="text-[var(--text-secondary)]">
                    <Filled text={t(begunText)} slot="when">
                      {when(begunAt)}
                    </Filled>
                  </span>
                )}
                {item.dueAt ? (
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1',
                      item.isOverdue ? 'font-medium text-error-fg dark:text-error-dark' : 'text-[var(--text-secondary)]',
                    )}
                  >
                    {item.isOverdue && (
                      <>
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span className="sr-only">{t('tickets.overdue')}: </span>
                      </>
                    )}
                    {/* One flex item: the sentence and its time stay inline together. */}
                    <span>
                      <Filled text={t('dash.workDeadline')} slot="when">
                        {when(item.dueAt)}
                      </Filled>
                    </span>
                  </span>
                ) : (
                  <span className="text-faint">{t('dash.noDeadline')}</span>
                )}
              </>
            )}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={statusTone(item.status)}>{status}</Badge>
          {(item.priority === 'URGENT' || item.priority === 'HIGH') && (
            <Badge tone={priorityTone(item.priority)}>{enumLabel(item.priority, locale)}</Badge>
          )}
        </span>
      </Link>
    </li>
  );
}
